import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import { useLazyResourceUserOtherProjectsAllocatedHoursSeries } from '~/modules/resourcing/common/hooks/useResourceUserOtherProjectsAllocatedHoursSeries';
import ResourceUserPeriodSummaryContent from './ResourceUserPeriodSummaryContent';
import useGetLazyAllocationSummary from './hooks/useGetLazyAllocationSummary';

const ResourceUserPeriodSummaryTooltip = ({
  children,
  endDate,
  holidays,
  isAllocationsLazyLoaded,
  resourceAllocations,
  resourceId,
  scheduledHours,
  startDate,
  timeOffs,
  totalAllocatedHours,
  open,
  onClose,
  periodResolution,
  showOtherProjectsRow
}) => {
  const {
    getCurrentAllocations,
    totalHoursByProject,
    loading
  } = useGetLazyAllocationSummary({
    endDate,
    resourceId,
    startDate
  });

  const {
    otherProjectsAllocatedHours,
    getOtherProjectsAllocatedHoursSeries,
    loading: otherProjectsLoading
  } = useLazyResourceUserOtherProjectsAllocatedHoursSeries({
    userUri: resourceId,
    dateRange: {
      startDate: startDate.toISO(),
      endDate: endDate.toISO()
    },
    periodResolution
  });

  const title = useMemo(
    () => (
      <ResourceUserPeriodSummaryContent
        resourceAllocations={
          !isAllocationsLazyLoaded ? resourceAllocations : []
        }
        scheduledHours={scheduledHours}
        totalAllocatedHours={totalAllocatedHours}
        totalHoursByProject={isAllocationsLazyLoaded ? totalHoursByProject : []}
        holidays={holidays}
        timeOffs={timeOffs}
        startDate={startDate}
        endDate={endDate}
        isAllocationsLazyLoaded={isAllocationsLazyLoaded}
        loading={loading || otherProjectsLoading}
        otherProjectsAllocatedHours={otherProjectsAllocatedHours}
        showOtherProjectsRow={showOtherProjectsRow}
      />
    ),
    [
      isAllocationsLazyLoaded,
      resourceAllocations,
      scheduledHours,
      totalAllocatedHours,
      totalHoursByProject,
      holidays,
      timeOffs,
      startDate,
      endDate,
      loading,
      otherProjectsLoading,
      otherProjectsAllocatedHours,
      showOtherProjectsRow
    ]
  );

  const handleTooltipOpen = useCallback(() => {
    if (isAllocationsLazyLoaded) {
      getCurrentAllocations();

      if (showOtherProjectsRow) {
        getOtherProjectsAllocatedHoursSeries();
      }
    }
  }, [
    isAllocationsLazyLoaded,
    getCurrentAllocations,
    showOtherProjectsRow,
    getOtherProjectsAllocatedHoursSeries
  ]);

  return (
    <Tooltip
      title={title}
      onOpen={isAllocationsLazyLoaded ? handleTooltipOpen : undefined}
      open={open}
      onClose={onClose}
    >
      {children}
    </Tooltip>
  );
};

ResourceUserPeriodSummaryTooltip.propTypes = {
  scheduledHours: PropTypes.number,
  totalAllocatedHours: PropTypes.number,
  resourceAllocations: PropTypes.array,
  holidays: PropTypes.array,
  timeOffs: PropTypes.array,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  children: PropTypes.node,
  resourceId: PropTypes.string,
  isAllocationsLazyLoaded: PropTypes.bool.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  periodResolution: PropTypes.string,
  showOtherProjectsRow: PropTypes.bool
};

export default ResourceUserPeriodSummaryTooltip;
